// i18n configuration for the CLIENT-FACING upload portal (UploadPortalPage) ONLY.
//
// Deliberately a SEPARATE i18next instance from `./index.ts` (the staff dashboard's en/es toggle).
// Christian, 2026-10-27: "Client-facing language set is {en, es, pt} from the client's CRM record
// via core ... read from core, never a local copy." That is a different axis from the staff
// toggle's "no default, staff choose their own, persisted per-person on the server" rule in
// `./index.ts` — a client visiting this portal has no staff account, no server-side preference row,
// and (critically) their language must eventually come from ptc-core, not from this browser's own
// localStorage/navigator detection. Reusing the staff instance would (a) add a language ('pt') the
// staff toggle never offers and was never scoped to review, and (b) let a client's OWN browser
// locale leak in via the staff detector's `navigator` order, which is a "local copy" of exactly the
// signal Christian ruled must come from core.
//
// DECOUPLED-PREP STATUS (ptc-core-3e's spec not yet out — see NotificationTemplates.cs for the
// same ruling on the backend side): this instance has NO detector at all and hard-defaults to
// 'en'. Nothing currently resolves a real client language and passes it in — when ptc-core's field
// is live, the portal's own session fetch (/portal/verify, /portal/returns, etc.) is where that
// value will come from, and UploadPortalPage will call `portalI18n.changeLanguage(...)` with it.
// Until then this is 100% behavior-preserving: every client sees English, exactly as before this
// file existed.
//
// es/pt content: machine-translated, PENDING REVIEW (Christian's ruling this leg — client-facing
// financial/tax correspondence needs a higher bar than the staff UI's "accepted-unreviewed"
// policy). Do not treat as shippable to real clients until reviewed.

import i18next from 'i18next';
import en from './locales/portal.en.json';
import es from './locales/portal.es.json';
import pt from './locales/portal.pt.json';

const portalI18n = i18next.createInstance();

// Deliberately NOT `.use(initReactI18next)` -- that plugin's init() calls react-i18next's
// `setI18n(instance)`, which is a MODULE-LEVEL GLOBAL, not scoped to this instance. Whichever
// i18next instance calls it LAST becomes the default every bare `useTranslation()` (no explicit
// `{ i18n }`) resolves to via react-i18next's `getI18n()` fallback -- including every STAFF
// component across the app, none of which know this instance exists. That is exactly what
// happened in production after this file was added (2026-10-28, PR #131): this instance's
// `.use(initReactI18next).init()` ran after the staff instance's (`./index.ts`), so the header
// toggle and feedback dialog -- both bare `useTranslation()` calls -- silently started resolving
// against THIS instance's `{ portal: {...} }` resource tree instead of the staff one, rendering
// raw dotted keys for every key outside it. UploadPortalPage already passes `{ i18n: portalI18n }`
// explicitly to every one of its `useTranslation()` calls (see line ~87 there), which resolves
// before react-i18next ever consults the global default (see react-i18next's useTranslation.js:
// `i18nFromProps || i18nFromContext || getI18n()`) -- so this instance never needed the plugin's
// global registration to work correctly. Caught by reproducing the raw key live in the deployed
// app and reading the actual React fiber's bound i18n instance, not by any static check: a static
// scan of `t()` call sites can't see which RUNTIME instance a bare `useTranslation()` resolves to.
void portalI18n.init({
  resources: {
    en: { translation: en },
    es: { translation: es },
    pt: { translation: pt },
  },
  lng: 'en',
  fallbackLng: 'en',
  supportedLngs: ['en', 'es', 'pt'],
  interpolation: { escapeValue: false }, // React escapes already.
  returnEmptyString: false,
});

export default portalI18n;
